import { useState } from 'react';
import Badge from './Badge';

/**
 * Reusable dual-section list component for System (read-only) and Custom (editable) items
 * Used by AI Parameters Modal and Conditions Tab
 */
export default function SystemCustomDualList({
  // Item data
  customItems = [],
  systemItems = [],

  // Labels
  itemTypeSingular = 'Item',  // e.g., "Parameter", "Condition"
  itemTypePlural = 'Items',    // e.g., "Parameters", "Conditions"
  customSectionDescription = 'Company-specific items you can create and edit',
  systemSectionDescription = 'Hardcoded system items (read-only)',

  // Callbacks
  onCreateCustom,
  onEditCustom,
  onDeleteCustom,

  // Render functions for custom content
  renderCustomItemHeader,     // (item) => JSX for item header (name, description, badges)
  renderCustomItemExpanded,   // (item) => JSX for expanded content
  renderSystemItemContent,    // (item) => JSX for system item content

  // Initial collapse state
  initialCustomExpanded = true,
  initialSystemExpanded = false,

  // Search
  searchQuery = '',

  // Loading state
  isLoading = false,

  // Empty states
  customEmptyMessage = 'No custom items yet. Click "Create" to add one.',
  systemEmptyMessage = 'No system items available',
}) {
  const [isCustomSectionExpanded, setIsCustomSectionExpanded] = useState(initialCustomExpanded);
  const [isSystemSectionExpanded, setIsSystemSectionExpanded] = useState(initialSystemExpanded);
  const [expandedItemId, setExpandedItemId] = useState(null);

  const toggleExpand = (itemId) => {
    setExpandedItemId(expandedItemId === itemId ? null : itemId);
  };

  // Filter by search
  const filterBySearch = (items, searchFields = ['name', 'description', 'label']) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item =>
      searchFields.some(field =>
        item[field]?.toLowerCase().includes(query)
      )
    );
  };

  const filteredCustomItems = filterBySearch(customItems);
  const filteredSystemItems = filterBySearch(systemItems);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="divide-y" style={{ borderColor: 'var(--color-border-default)' }}>
      {/* Custom Items Section */}
      <div className="border-b" style={{ borderColor: 'var(--color-border-default)' }}>
        {/* Section Header */}
        <button
          onClick={() => setIsCustomSectionExpanded(!isCustomSectionExpanded)}
          className="w-full flex items-center justify-between p-4 transition-colors"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div className="flex items-center gap-3">
            <svg
              className={`w-5 h-5 transition-transform ${isCustomSectionExpanded ? 'rotate-90' : ''}`}
              style={{ color: 'var(--color-text-muted)' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <div className="text-left">
              <h4 className="font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                Custom {itemTypePlural}
                <Badge variant="info">{filteredCustomItems.length}</Badge>
              </h4>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{customSectionDescription}</p>
            </div>
          </div>
          {onCreateCustom && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCreateCustom();
              }}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all duration-200 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create
            </button>
          )}
        </button>

        {/* Custom Items List */}
        {isCustomSectionExpanded && (
          <div className="border-l-4 ml-4" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-info)' }}>
            {filteredCustomItems.length === 0 ? (
              <div className="text-center py-8 px-4" style={{ color: 'var(--color-text-muted)' }}>
                <p className="text-sm">
                  {searchQuery ? `No custom ${itemTypePlural.toLowerCase()} match your search` : customEmptyMessage}
                </p>
              </div>
            ) : (
              <div className="divide-y pl-4" style={{ borderColor: 'var(--color-border-subtle)' }}>
                {filteredCustomItems.map((item) => {
                  const isExpanded = expandedItemId === item.id;
                  return (
                    <div key={item.id}>
                      {/* Item Header */}
                      <div
                        onClick={() => toggleExpand(item.id)}
                        className="flex items-center gap-3 p-4 transition-colors cursor-pointer"
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <div style={{ color: 'var(--color-text-muted)' }}>
                          <svg
                            className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>

                        {/* Custom header content */}
                        {renderCustomItemHeader ? renderCustomItemHeader(item) : (
                          <div className="flex-1 min-w-0">
                            <h5 className="text-white font-medium truncate">{item.name || item.label}</h5>
                            {item.description && (
                              <p className="text-sm text-gray-400 truncate">{item.description}</p>
                            )}
                          </div>
                        )}

                        {/* Delete Button */}
                        {onDeleteCustom && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteCustom(item);
                            }}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                            title={`Delete ${itemTypeSingular.toLowerCase()}`}
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && renderCustomItemExpanded && (
                        <div className="p-4" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                          {renderCustomItemExpanded(item)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* System Items Section */}
      <div>
        {/* Section Header */}
        <button
          onClick={() => setIsSystemSectionExpanded(!isSystemSectionExpanded)}
          className="w-full flex items-center justify-between p-4 transition-colors"
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div className="flex items-center gap-3">
            <svg
              className={`w-5 h-5 transition-transform ${isSystemSectionExpanded ? 'rotate-90' : ''}`}
              style={{ color: 'var(--color-text-muted)' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <div className="text-left">
              <h4 className="font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                System {itemTypePlural}
                <Badge variant="warning">{filteredSystemItems.length}</Badge>
              </h4>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{systemSectionDescription}</p>
            </div>
          </div>
          <div className="text-xs flex items-center gap-1" style={{ color: 'var(--color-warning)' }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Read Only
          </div>
        </button>

        {/* System Items List */}
        {isSystemSectionExpanded && (
          <div className="border-l-4 ml-4" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-warning)' }}>
            {filteredSystemItems.length === 0 ? (
              <div className="text-center py-8 px-4" style={{ color: 'var(--color-text-muted)' }}>
                <p className="text-sm">
                  {searchQuery ? `No system ${itemTypePlural.toLowerCase()} match your search` : systemEmptyMessage}
                </p>
              </div>
            ) : (
              <div className="divide-y pl-4" style={{ borderColor: 'var(--color-border-subtle)' }}>
                {filteredSystemItems.map((item) => (
                  <div key={item.id || item.name} className="flex items-center gap-3 p-4">
                    {/* No chevron - not expandable */}
                    <div className="w-5"></div>

                    {/* System item content */}
                    {renderSystemItemContent ? renderSystemItemContent(item) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-white font-medium truncate">{item.name || item.label}</h5>
                          {item.description && (
                            <p className="text-sm text-gray-400 truncate">{item.description}</p>
                          )}
                        </div>
                        {item.type && <Badge variant="default">{item.type}</Badge>}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
